import { ChevronRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'
import {
  Avatar,
  Button,
  Card,
  CardBody,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  ScrollShadow,
  Tooltip,
} from '@heroui/react'
import { useLogout } from '@qse/auth-ui'
import { User, UserRoleEnum } from '@qse/constants'
import { useIsOverQuota, useUIStore } from '@qse/stores'
import { detectCurrentApp, getAdminUrl } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { ShieldCheckIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { DualProgressBar } from './components/dual-progress-bar'
import { useFolderUpload } from './hooks/useFolderUpload'
import { useGlobalFileUpload } from './hooks/useGlobalFileUpload'
import { useJiraFeedback } from './hooks/useJiraFeedback'
import {
  AddIcon,
  CloudIcon,
  CreateNewFolder,
  LogoIcon,
  NewFileUpload,
  NewFolderUpload,
  QuestionIcon,
} from './icons'
import { ThemeSwitch } from './theme-switch'

/** Format user role for display */
const formatUserRole = (userRole: UserRoleEnum): string => {
  switch (userRole) {
    case UserRoleEnum.SUPER_ADMIN:
      return 'Super Admin'
    case UserRoleEnum.TENANT_ADMIN:
      return 'Tenant Admin'
    case UserRoleEnum.TENANT_USER:
      return 'Tenant User'
    default:
      return 'User'
  }
}

export interface MenuItem {
  id: string
  label: string
  icon: React.ElementType
  isPrimary?: boolean
  hasArrow?: boolean
  hasAvatar?: boolean
  component?: React.ReactNode
  redirectUrl?: string
  isExternal?: boolean
  externalIcon?: React.ElementType
}

export interface SidebarProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  menuItems: MenuItem[]
  securitySubmenuItems?: Array<{
    label: string
    redirectUrl: string
  }>
  showSecurityTab?: boolean
  storageInfo?: {
    used: number
    remaining: number
    total: number
    percentage?: number
    isUploading?: boolean
    isDeleting?: boolean
    pendingBytes?: number
    usedPercent?: number
  }
  apiQuotaInfo?: {
    used: number
    total: number
    percentage?: number
    isActive?: boolean
  }
  user: User
}

const Sidebar = ({
  isOpen,
  menuItems,
  securitySubmenuItems = [],
  showSecurityTab = true,
  storageInfo,
  apiQuotaInfo,
  user,
}: SidebarProps) => {
  const { disableNewButton, isSystemFolder } = useUIStore()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ id: string }>()
  const { addFiles } = useGlobalFileUpload()
  const queryClient = useQueryClient()
  const isOverQuota = useIsOverQuota()

  const {
    fileInputRef,
    handleFileUpload,
    handleFolderUpload, // FS Access–aware; falls back to input when needed
    handleFileChange, // used by hidden input (fallback)
    handleFolderCreation,
  } = useFolderUpload({
    pathname: location.pathname,
    currentFolderId: params.id,
    queryClient,
    addFiles,
  })

  const [securityOpen, setSecurityOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false)
  // const [isAppDownloadDropdownOpen, setIsAppDownloadDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const mobileDropdownRef = useRef<HTMLDivElement>(null)
  // const appDownloadDropdownRef = useRef<HTMLDivElement>(null)
  const { handleLogout } = useLogout()
  const { openFeedbackDialog } = useJiraFeedback()
  const appDetection = detectCurrentApp()
  const domain = appDetection.appType === 'admin' ? 'admin' : 'dashboard'

  const dropdownItems = [
    {
      label: 'New Folder',
      key: 'new-folder',
      startContent: <CreateNewFolder className="w-4 h-4" />,
      onAction: handleFolderCreation,
    },
    {
      label: 'File Upload',
      key: 'file-upload',
      startContent: <NewFileUpload className="w-4 h-4" />,
      onAction: handleFileUpload,
    },
    {
      label: 'Folder Upload',
      key: 'folder-upload',
      startContent: <NewFolderUpload className="w-4 h-4" />,
      onAction: handleFolderUpload, // important: uses FS Access path
    },
  ]

  // const appDownloadItems = [
  //   {
  //     key: 'app-store',
  //     icon: <img
  //       alt="Download on the App Store"
  //       className="h-10 w-auto"
  //       loading="lazy"
  //       src={appStoreBadge}
  //     />,
  //     href: getAppStoreUrl( APP_STORE_CONFIG.APPLE.APP_ID, APP_STORE_CONFIG.APPLE.WEB_URL,),
  //   },
  //   {
  //     key: 'play-store',
  //     icon: <img
  //       alt="Get it on Google Play"
  //       className="h-10 w-auto"
  //       loading="lazy"
  //       src={googlePlayBadge}
  //     />,
  //     href: getPlayStoreUrl(
  // APP_STORE_CONFIG.GOOGLE_PLAY.PACKAGE_ID,
  // APP_STORE_CONFIG.GOOGLE_PLAY.WEB_URL,
  //     ),
  //   },
  // ]

  // Handle right-click outside dropdown to close it
  useEffect(() => {
    const handleRightClick = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
      if (
        isMobileDropdownOpen &&
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMobileDropdownOpen(false)
      }
    }

    if (isDropdownOpen || isMobileDropdownOpen) {
      document.addEventListener('contextmenu', handleRightClick)
    }

    return () => {
      document.removeEventListener('contextmenu', handleRightClick)
    }
  }, [isDropdownOpen, isMobileDropdownOpen])

  const menuItem = useCallback(
    ({ item }: { item: MenuItem }) => {
      // Determine if this item should be active
      let isActive = false

      if (item.redirectUrl) {
        isActive = location.pathname === item.redirectUrl
      } else {
        // Fallback to old logic for backward compatibility
        if (item.id === 'favorites' && location.pathname.includes('/favorites')) isActive = true
        if (item.id === 'trash' && location.pathname.includes('/trash')) isActive = true
        if (item.id === 'my-drive' && location.pathname.includes('/dashboard')) isActive = true
      }

      return {
        primary: (
          <div key={item.id}>
            {item.id === 'dashboard' ? (
              <Button
                className={`relative bg-blue-600 dark:bg-[#3451C8] rounded-2xl h-[68px] ${isOpen ? 'w-11/12' : 'w-12 h-12'} m-auto flex mb-4 ${isOpen ? 'justify-start gap-4' : 'justify-center'} font-medium text-white hover:bg-blue-500/80 transition-all duration-200`}
                endContent={
                  item.isExternal &&
                  item.externalIcon && (
                    <item.externalIcon className="absolute right-4 top-1/2 -translate-y-1/2 text-white" />
                  )
                }
                startContent={
                  <div className="flex items-center justify-center rounded-xl h-[44px] w-[44px] bg-gray-900 dark:bg-black p-2">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                }
                onPress={() => {
                  if (item.redirectUrl) {
                    window.open(item.redirectUrl, '_blank')
                  }
                }}
              >
                {item.label}
              </Button>
            ) : (
              <Tooltip
                content="This action is disabled for conversation folders."
                isDisabled={!isSystemFolder}
              >
                <div ref={dropdownRef}>
                  <Dropdown
                    isDisabled={disableNewButton}
                    isOpen={isDropdownOpen}
                    placement="right"
                    classNames={{
                      content:
                        'bg-white dark:bg-[#343537] border border-gray-100 dark:border-[#707070]',
                    }}
                    onOpenChange={setIsDropdownOpen}
                  >
                    <DropdownTrigger>
                      <Button
                        className={`relative bg-blue-600 dark:bg-[#3451C8] rounded-2xl h-[68px] ${isOpen ? 'w-11/12' : 'w-12 h-12'} m-auto flex mb-4 ${isOpen ? 'justify-start gap-4' : 'justify-center'} font-medium text-white hover:bg-blue-500/80 transition-all duration-200`}
                        isDisabled={disableNewButton}
                        startContent={
                          <div className="flex items-center justify-center rounded-xl h-[44px] w-[44px] bg-gray-900 dark:bg-black p-2">
                            <AddIcon className="w-5 h-5 text-white" />
                          </div>
                        }
                      >
                        {isOpen ? 'New' : ''}
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Static Actions"
                      disabledKeys={
                        isOverQuota ? new Set(['file-upload', 'folder-upload']) : undefined
                      }
                    >
                      {dropdownItems?.map(di => {
                        const disabled =
                          isOverQuota && (di.key === 'file-upload' || di.key === 'folder-upload')

                        return (
                          <DropdownItem
                            key={di.key}
                            className="pointer-events-auto"
                            onPress={() => {
                              if (disabled) return
                              di.onAction?.()
                            }}
                          >
                            <div
                              className="flex items-center gap-2"
                              title={
                                disabled
                                  ? 'Storage quota exceeded. Delete files to free up space.'
                                  : undefined
                              }
                            >
                              {di.startContent}
                              <p>{di.label}</p>
                            </div>
                          </DropdownItem>
                        )
                      })}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </Tooltip>
            )}
            {user?.userRole !== UserRoleEnum.TENANT_ADMIN &&
              appDetection.appType === 'dashboard' && (
                <Divider className="bg-gray-100 dark:bg-white/10" />
              )}
          </div>
        ),
        secondary: (
          <Button
            key={item.id}
            {...(item.isExternal
              ? {
                  onPress: () => {
                    if (item.redirectUrl) {
                      window.open(item.redirectUrl, '_blank')
                    }
                  },
                }
              : {
                  as: Link,
                  to: item.redirectUrl || '',
                })}
            radius="lg"
            variant="light"
            className={`w-full h-auto p-0 rounded-none ${isOpen ? 'px-3' : 'px-2'} transition-all duration-200 ${
              isActive
                ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 dark:border-blue-400'
                : 'hover:bg-gray-50 dark:hover:bg-white/5'
            }`}
          >
            <div
              className={`flex items-center ${isOpen ? 'justify-between' : 'justify-center'} w-full p-2`}
            >
              <div className={`flex items-center w-full text-start ${isOpen ? 'gap-3' : 'gap-0'}`}>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    isActive ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-200 dark:bg-white/10'
                  }`}
                >
                  <item.icon
                    className={`w-5 h-5 transition-colors duration-200 ${
                      isActive
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-white'
                    }`}
                  />
                </div>
                {isOpen && (
                  <div className="flex-1 w-full">
                    <span
                      className={`font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                        isActive
                          ? 'text-blue-600 dark:text-blue-400 font-semibold'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                )}
              </div>
              {isOpen && (
                <div className="flex items-center gap-2">
                  {item.hasAvatar && (
                    <Avatar
                      className="w-8 h-8"
                      size="sm"
                      classNames={{
                        base: 'bg-gradient-to-r from-red-500 to-pink-500',
                      }}
                    />
                  )}
                  {item.hasArrow && (
                    <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-white/60" />
                  )}
                </div>
              )}
            </div>
          </Button>
        ),
      }
    },
    [
      isOpen,
      location,
      appDetection.appType,
      handleFileUpload,
      handleFolderUpload,
      handleFolderCreation,
      isDropdownOpen,
      isSystemFolder,
      user?.userRole,
      disableNewButton,
      dropdownItems,
      isOverQuota,
    ],
  )

  return (
    <>
      <div
        className={`
          h-screen w-[265px] flex-col bg-white dark:bg-[#1a1a1a] border-r border-gray-200 dark:border-white/10
          transition-all duration-300 ease-in-out
          lg:flex hidden
        `}
      >
        {/* Header */}
        <div className={`p-4 pb-[30px] transition-all duration-200`}>
          <div className={`flex items-center ${isOpen ? 'gap-3' : 'justify-center'}`}>
            {isOpen && (
              <div className="flex flex-col items-start gap-1">
                <LogoIcon className="text-black dark:text-white" />
                {user?.userRole && (
                  <span className="text-xs text-gray-600 dark:text-white/60 font-medium">
                    {formatUserRole(user.userRole)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Primary Menu Item */}
        <div className="px-0">
          {menuItems.filter(item => item.isPrimary).map(item => menuItem({ item }).primary)}
        </div>

        {/* Go to Admin - Tenant Admin on Dashboard only */}
        {user?.userRole === UserRoleEnum.TENANT_ADMIN && appDetection.appType === 'dashboard' && (
          <div className="px-0">
            <Button
              className={`relative bg-orange-600 dark:bg-orange-500 rounded-2xl h-[68px] ${isOpen ? 'w-11/12' : 'w-12 h-12'} m-auto flex mb-4 ${isOpen ? 'justify-start gap-4' : 'justify-center'} font-medium text-white hover:bg-orange-500/80 dark:hover:bg-orange-400/80 transition-all duration-200`}
              startContent={
                <div className="flex items-center justify-center rounded-xl h-[44px] w-[44px] bg-gray-900 dark:bg-black p-2">
                  <Cog6ToothIcon className="w-6 h-6 text-white" />
                </div>
              }
              onPress={() => {
                const adminUrl = getAdminUrl('/auth/login')

                window.open(adminUrl, '_blank')
              }}
            >
              {isOpen ? 'Go to Admin' : ''}
            </Button>
            <Divider className="bg-gray-100 dark:bg-white/10" />
          </div>
        )}

        {/* Secondary Menu Items */}
        <ScrollShadow hideScrollBar className="flex-1 px-0 overflow-y-auto mb-8">
          <div className="space-y-3 mt-3">
            {menuItems
              .filter(item => !item.isPrimary && item.id !== 'profile')
              .map(item => menuItem({ item }).secondary)}
            <Divider className="bg-gray-100 dark:bg-white/10" />

            {showSecurityTab && (
              <>
                <Button
                  radius="lg"
                  variant="light"
                  className={`w-full h-auto px-2 rounded-none hover:bg-gray-100 dark:hover:bg-white/10 transition-all duration-200 ${
                    securitySubmenuItems.some(item => location.pathname === item.redirectUrl)
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 dark:border-blue-400'
                      : ''
                  }`}
                  endContent={
                    <ChevronRightIcon
                      className={`w-5 h-5 mr-2 text-gray-500 dark:text-white/60 transition-transform duration-200 ${securityOpen ? 'rotate-90' : ''}`}
                    />
                  }
                  onPress={() => {
                    setSecurityOpen(v => !v)
                    if (!securityOpen) {
                      setTimeout(() => {
                        const el = document.querySelector('[data-security-submenu]')

                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                        }
                      }, 100)
                    }
                  }}
                >
                  <div
                    className={`flex items-center ${isOpen ? 'justify-between' : 'justify-center'} w-full p-3`}
                  >
                    <div className={`flex items-center ${isOpen ? 'gap-3' : 'gap-0'}`}>
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                          securitySubmenuItems.some(item => location.pathname === item.redirectUrl)
                            ? 'bg-blue-100 dark:bg-blue-900/30'
                            : 'bg-gray-100 dark:bg-white/10'
                        }`}
                      >
                        <ShieldCheckIcon
                          className={`w-5 h-5 transition-colors duration-200 ${
                            securitySubmenuItems.some(
                              item => location.pathname === item.redirectUrl,
                            )
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-white'
                          }`}
                        />
                      </div>
                      {isOpen && (
                        <span
                          className={`font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                            securitySubmenuItems.some(
                              item => location.pathname === item.redirectUrl,
                            )
                              ? 'text-blue-600 dark:text-blue-400 font-semibold'
                              : 'text-gray-900 dark:text-white'
                          }`}
                        >
                          Security
                        </span>
                      )}
                    </div>
                  </div>
                </Button>
                {securityOpen && isOpen && (
                  <div data-security-submenu className="pl-[75px] flex flex-col gap-6">
                    {securitySubmenuItems.map((item, index) => (
                      <Link
                        key={index}
                        className="text-gray-700 dark:text-white text-sm text-left font-normal hover:underline"
                        to={item.redirectUrl}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollShadow>

        <div>
          {/* Storage Section */}
          {storageInfo && user?.userRole !== UserRoleEnum.SUPER_ADMIN && domain === 'dashboard' && (
            <Card className="bg-inherit shadow-none px-1 rounded-none border-none transition-all duration-200 mb-1">
              <CardBody className="py-0">
                <div
                  className={`flex items-start ${isOpen ? 'gap-3' : 'justify-center'} w-full p-1`}
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CloudIcon className="w-5 h-5 text-gray-700 dark:text-white" />
                  </div>
                  {isOpen && (
                    <Tooltip
                      isDisabled={!isOverQuota}
                      content={
                        <div className="max-w-[280px] whitespace-normal">
                          {storageInfo.used === storageInfo.total
                            ? 'Your storage is full. Delete files to free up space and continue uploading.'
                            : 'You have exceeded your storage quota due to your plan change. Delete files to restore upload and copy operations.'}
                        </div>
                      }
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-900 dark:text-white font-medium text-sm">
                            <span className="font-semibold">Storage</span>
                            <span className="font-normal">
                              {' '}
                              {storageInfo.total === 0 && storageInfo.used > 0
                                ? '(Over quota)'
                                : `(${storageInfo.percentage}% full)`}
                            </span>
                          </span>
                        </div>
                        <div>
                          <DualProgressBar
                            allocated={storageInfo.pendingBytes}
                            className="mb-2"
                            height="h-2"
                            isOverQuota={isOverQuota}
                            pendingBytes={storageInfo.pendingBytes}
                            showValueDisplay={true}
                            total={storageInfo.total}
                            used={storageInfo.used}
                            usedPercentage={storageInfo.usedPercent}
                          />
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* API Quota Section */}
          {apiQuotaInfo &&
            user?.userRole !== UserRoleEnum.SUPER_ADMIN &&
            domain === 'dashboard' && (
              <div className="mb-1">
                <Card className="bg-inherit shadow-none px-1 rounded-none border-none transition-all duration-200">
                  <CardBody className="py-0">
                    <div
                      className={`flex items-start ${isOpen ? 'gap-3' : 'justify-center'} w-full p-1`}
                    >
                      <div className="w-10 h-10 bg-gray-100 dark:bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-gray-700 dark:text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                      </div>
                      {isOpen && (
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-900 dark:text-white font-medium text-sm">
                              <span className="font-semibold">API Quota</span>
                              <span className="font-normal">
                                {' '}
                                ({apiQuotaInfo.percentage}% used)
                              </span>
                            </span>
                          </div>
                          <Tooltip
                            isDisabled={!((apiQuotaInfo.percentage || 0) >= 100)}
                            content={
                              <div className="max-w-[280px] whitespace-normal">
                                {apiQuotaInfo.used === apiQuotaInfo.total
                                  ? 'Your API quota is full. Your usage will reset on the next cycle.'
                                  : 'You have exceeded your API quota. Your usage will reset on the next cycle.'}
                              </div>
                            }
                          >
                            <div>
                              <DualProgressBar
                                allocatedColor="bg-green-200 dark:bg-green-300/30"
                                className="mb-2"
                                formatTotalValue={value => `${value.toLocaleString()} calls`}
                                formatUsedValue={value => `${value.toLocaleString()} calls`}
                                height="h-2"
                                isOverQuota={(apiQuotaInfo.percentage || 0) >= 100}
                                showValueDisplay={false}
                                total={apiQuotaInfo.total}
                                trackColor="bg-gray-100 dark:bg-white/10"
                                unit="calls"
                                used={apiQuotaInfo.used}
                                usedColor="bg-green-600 dark:bg-green-400"
                                usedPercentage={apiQuotaInfo.percentage}
                              />
                            </div>
                          </Tooltip>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 dark:text-white/60">
                              {apiQuotaInfo.used.toLocaleString()} /{' '}
                              {apiQuotaInfo.total.toLocaleString()} calls
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
                {apiQuotaInfo.isActive && (
                  <div className="flex items-center gap-1 text-xs px-4 py-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-green-500">API Active</span>
                  </div>
                )}
              </div>
            )}

          {/* App Download Links - Drive Only */}
          {/* {appDetection.appType === 'dashboard' && isOpen && (
            <div ref={appDownloadDropdownRef}>
              <Dropdown
                isOpen={isAppDownloadDropdownOpen}
                placement="right"
                classNames={{
                  content:
                    'bg-white dark:bg-[#343537] border border-gray-100 dark:border-[#707070] w-fit min-w-[150px] p-[2px]',
                }}
                onOpenChange={setIsAppDownloadDropdownOpen}
              >
                <DropdownTrigger>
                  <Button
                    className="w-full h-auto p-0 rounded-none px-3 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-white/5"
                    radius="lg"
                    variant="light"
                  >
                    <div className="flex items-center justify-between w-full p-2">
                      <div className="flex items-center w-full text-start gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-200 dark:bg-white/10">
                          <MobileIcon className="w-5 h-5 text-gray-700 dark:text-white" />
                        </div>
                        <div className="flex-1 w-full">
                          <span className="font-medium text-sm text-gray-900 dark:text-white">
                            Get the Mobile App
                          </span>
                        </div>
                      </div>
                      <ChevronRightIcon
                        className={`w-4 h-4 text-gray-500 dark:text-white/60 transition-transform duration-200 ${isAppDownloadDropdownOpen ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="App Download Actions" classNames={{
                  base: "gap-2 min-w-[150px]",
                  list: "w-[150px]"
                }}>
                  {appDownloadItems.map(item => (
                    <DropdownItem
                      key={item.key}
                      className="pointer-events-auto"
                      onPress={() => {
                        if (item.href) {
                          window.open(item.href, '_blank', 'noopener,noreferrer')
                        }
                      }}
                    >
                      {item.icon}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          )} */}

          <Card className="bg-inherit shadow-none px-1 rounded-none border-none transition-all duration-200 cursor-pointer">
            <CardBody>
              <button
                className={`flex items-center ${isOpen ? 'gap-3' : 'justify-center'} w-full p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg`}
                type="button"
                onClick={() => {
                  openFeedbackDialog()
                }}
              >
                <div className="w-10 h-10 bg-gray-100 dark:bg-white/10 rounded-lg flex items-center justify-center">
                  <QuestionIcon className="w-5 h-5 text-gray-700 dark:text-white" />
                </div>
                {isOpen && (
                  <span className="text-gray-900 dark:text-white font-medium text-sm">
                    Feedback
                  </span>
                )}
              </button>
            </CardBody>
          </Card>

          {isOpen && (
            <div className="flex items-center justify-between px-6 py-4">
              <span className="text-gray-900 dark:text-white font-medium text-sm">Mode</span>
              <ThemeSwitch className="p-2 bg-blue-600 dark:bg-[#3451C8] rounded-md !text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 px-2 right-0 z-50 bg-white dark:bg-[#1a1a1a] border-t border-gray-300 dark:border-white/10 lg:hidden flex justify-around items-center h-20">
        {menuItems
          .filter(item => !item.isPrimary && item.id !== 'profile')
          .map(item => {
            const isActive = item.redirectUrl ? location.pathname === item.redirectUrl : false

            return (
              <motion.button
                key={item.id}
                className="flex relative gap-2 flex-col items-center h-full justify-center flex-1 focus:outline-none transition-all duration-200"
                onClick={() => {
                  setSecurityOpen(false)
                  navigate(item.redirectUrl || '/')
                }}
              >
                {isActive && (
                  <motion.span
                    className="absolute inset-0 z-0 border-t-4 w-[64px] h-fit rounded-full border-blue-600 dark:border-white mx-auto left-0 right-0"
                    layoutId="pill-tab"
                    transition={{ type: 'spring', duration: 0.5 }}
                  />
                )}
                <item.icon
                  className={`w-7 h-7 transition-colors duration-200 ${isActive ? '' : 'text-gray-600 dark:text-white/50'}`}
                />
                <span
                  className={`text-xs font-medium transition-colors duration-200 ${isActive ? 'dark:text-white text-gray-800 font-semibold' : 'text-gray-600 dark:text-white/50'}`}
                >
                  {item.label}
                </span>
              </motion.button>
            )
          })}

        {/* Security Menu (Mobile) */}
        {showSecurityTab && (
          <Dropdown placement="top">
            <DropdownTrigger>
              <motion.button
                className="flex relative gap-2 flex-col items-center h-full justify-center flex-1 focus:outline-none transition-all duration-200"
                type="button"
              >
                {securityOpen ||
                  (securitySubmenuItems.some(item => location.pathname === item.redirectUrl) && (
                    <motion.span
                      className="absolute inset-0 z-0 border-t-4 w-[64px] h-fit rounded-full border-blue-600 dark:border-white mx-auto left-0 right-0"
                      layoutId="pill-tab"
                      transition={{ type: 'spring', duration: 0.5 }}
                    />
                  ))}
                <ShieldCheckIcon
                  className={`w-7 h-7 transition-colors duration-200 ${
                    securitySubmenuItems.some(item => location.pathname === item.redirectUrl)
                      ? ''
                      : 'text-gray-600 dark:text-white/50'
                  }`}
                />
                <span
                  className={`text-xs font-medium transition-colors duration-200 ${
                    securitySubmenuItems.some(item => location.pathname === item.redirectUrl)
                      ? 'dark:text-white text-gray-800 font-semibold'
                      : 'text-gray-600 dark:text-white/50'
                  }`}
                >
                  Security
                </span>
              </motion.button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Security Actions">
              {securitySubmenuItems.map((si, index) => (
                <DropdownItem key={index} onPress={() => navigate(si.redirectUrl)}>
                  {si.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}

        {/* Feedback (Mobile) */}
        <motion.button
          className="flex relative gap-2 flex-col items-center h-full justify-center flex-1 focus:outline-none transition-all duration-200"
          onClick={() => {
            openFeedbackDialog()
          }}
        >
          <QuestionIcon className="w-7 h-7 text-gray-600 dark:text-white/50" />
          <span className="text-xs font-medium text-gray-600 dark:text-white/50">Feedback</span>
        </motion.button>

        {/* Profile (Mobile) */}
        {menuItems
          .filter(item => item.id === 'profile')
          .map(item => {
            const isActive = item.redirectUrl ? location.pathname === item.redirectUrl : false

            return (
              <Dropdown key={item.id}>
                <DropdownTrigger>
                  <motion.button
                    className="flex relative gap-2 flex-col items-center h-full justify-center flex-1 focus:outline-none transition-all duration-200"
                    type="button"
                  >
                    {isActive && (
                      <motion.span
                        className="absolute inset-0 z-0 border-t-4 w-[64px] h-fit rounded-full border-blue-600 dark:border-white mx-auto left-0 right-0"
                        layoutId="pill-tab"
                        transition={{ type: 'spring', duration: 0.5 }}
                      />
                    )}
                    <item.icon
                      className={`w-7 h-7 transition-colors duration-200 ${isActive ? '' : 'text-gray-600 dark:text-white/50'}`}
                    />
                    <span
                      className={`text-xs font-medium transition-colors duration-200 ${isActive ? '' : 'text-gray-600 dark:text-white/50'}`}
                    >
                      {item.label}
                    </span>
                  </motion.button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Profile Actions">
                  <DropdownItem
                    key="profile"
                    onPress={() => navigate(item.redirectUrl || '/profile')}
                  >
                    Profile
                  </DropdownItem>
                  <DropdownItem
                    key="logout"
                    className="text-danger"
                    color="danger"
                    onPress={() => handleLogout()}
                  >
                    Logout
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )
          })}
      </nav>

      <div className="fixed top-4 right-4 z-[1000] lg:hidden">
        <ThemeSwitch className="p-2 bg-blue-600 dark:bg-[#3451C8] !text-white rounded-full" />
      </div>

      {/* Mobile floating action (New) */}
      {!(
        location.pathname.includes('/dashboard/management/plan') ||
        location.pathname.includes('/dashboard/management/tenants') ||
        location.pathname.includes('/dashboard/management/users') ||
        location.pathname.includes('/profile') ||
        location.pathname.includes('/dashboard/generate-keys') ||
        location.pathname.includes('/dashboard/generate-entropy')
      ) && (
        <div className="fixed bottom-24 right-4 z-50 lg:hidden">
          <div ref={mobileDropdownRef}>
            <Dropdown
              isOpen={isMobileDropdownOpen}
              placement="top"
              classNames={{
                content: 'dark:bg-[#343537] border dark:border-[#707070]',
              }}
              onOpenChange={setIsMobileDropdownOpen}
            >
              <DropdownTrigger>
                <Button isIconOnly className="bg-blue-600 dark:bg-[#3451C8]">
                  <AddIcon className="w-5 h-5 text-white" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Static Actions"
                disabledKeys={isOverQuota ? new Set(['file-upload', 'folder-upload']) : undefined}
              >
                <DropdownItem key="new-folder" title="New Folder" onPress={handleFolderCreation} />
                <DropdownItem
                  key="file-upload"
                  className="pointer-events-auto"
                  onPress={() => {
                    if (isOverQuota) return
                    handleFileUpload()
                  }}
                >
                  <p
                    title={
                      isOverQuota
                        ? 'Storage quota exceeded. Delete files to free up space.'
                        : 'File Upload'
                    }
                  >
                    File Upload
                  </p>
                </DropdownItem>
                <DropdownItem
                  key="folder-upload"
                  className="pointer-events-auto"
                  onPress={() => {
                    if (isOverQuota) return
                    handleFolderUpload()
                  }}
                >
                  <p
                    title={
                      isOverQuota
                        ? 'Storage quota exceeded. Delete files to free up space.'
                        : 'Folder Upload'
                    }
                  >
                    Folder Upload
                  </p>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      )}

      {/* Hidden File Input (fallback path; hook toggles webkitdirectory as needed) */}
      <input
        ref={fileInputRef}
        multiple
        accept="*/*"
        className="hidden"
        type="file"
        onChange={handleFileChange}
      />
    </>
  )
}

export default Sidebar
